import { useEffect, useState } from "react";
import {
  useGetIngredientsSupplierList,
  useGetIngredientWholeList
} from "../../../../api/ingredients";
import {
  IngredientDataTypeLabel,
  IngredientFilterKeys
} from "../../../../config/enums/IngredientType.enum";
import { saveIngredientFilters } from "../../../../utils/cacheStorage";
import { FilterOption } from "../types/ingredientListView.types";

const DEFAULT_SORT = 3;

const readStoredIds = (key: string): string[] => {
  const value = localStorage.getItem(key);
  return value ? JSON.parse(value) : [];
};

const toggleValue = (values: string[], value: string) =>
  values.includes(value) ? values.filter(v => v !== value) : [...values, value];

export interface IngredientFilterState {
  ingredientOptions: FilterOption[];
  supplierOptions: FilterOption[];
  dataTypeOptions: FilterOption[];
  selectedIngredients: string[];
  selectedSuppliers: string[];
  selectedDataTypes: string[];
  sortBy: number;
  toggleIngredient: (value: string) => void;
  toggleSupplier: (value: string) => void;
  toggleDataType: (value: string) => void;
  setSortBy: (value: number) => void;
  reset: () => void;
  apply: () => void;
}

/**
 * Tailwind replacement for the state/localStorage-restore logic in the
 * legacy `IngredientFilter` (see
 * src/screens/ingredients/ingredientList/IngredientFilter.tsx). Same
 * persisted filter keys, same reset/apply semantics — only the underlying
 * selection shape moved from react-select's `{value,label}[]` objects to
 * plain `string[]` ids, since the new panel renders its own checkbox lists.
 */
export const useIngredientFilterState = (
  refetchWithFilters: (
    ingredientIds: string[],
    supplierIds: string[],
    dataTypeIds: string[],
    sortBy: number
  ) => void
): IngredientFilterState => {
  const [selectedIngredients, setSelectedIngredients] = useState<string[]>([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [selectedDataTypes, setSelectedDataTypes] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<number>(DEFAULT_SORT);

  useEffect(() => {
    const ingredientIds = readStoredIds(IngredientFilterKeys.INGREDIENT_IDS);
    const supplierIds = readStoredIds(IngredientFilterKeys.SUPPLIER_IDS);
    const dataTypeIds = readStoredIds(IngredientFilterKeys.DATA_TYPE_IDS);
    const status = localStorage.getItem(IngredientFilterKeys.STATUS);

    if (ingredientIds.length || supplierIds.length || dataTypeIds.length || status !== null) {
      setSelectedIngredients(ingredientIds);
      setSelectedSuppliers(supplierIds);
      setSelectedDataTypes(dataTypeIds);
      setSortBy(status ? Number(status) : DEFAULT_SORT);
    }
  }, []);

  const { data: ingredientListByProduct } = useGetIngredientWholeList(false);
  const { data: ingredientSupplierList } = useGetIngredientsSupplierList(false);

  const ingredientOptions: FilterOption[] = (ingredientListByProduct || []).map((item: any) => ({
    value: item.id,
    label: item.title
  }));
  const supplierOptions: FilterOption[] = (ingredientSupplierList || []).map((item: any) => ({
    value: item.id,
    label: item.title
  }));
  const dataTypeOptions: FilterOption[] = Object.entries(IngredientDataTypeLabel).map(
    ([value, label]) => ({ value, label })
  );

  const reset = () => {
    setSelectedIngredients([]);
    setSelectedSuppliers([]);
    setSelectedDataTypes([]);
    setSortBy(DEFAULT_SORT);
    // saveIngredientFilters is typed for number[] ids elsewhere in the app;
    // ingredient/supplier ids here are Mongo ObjectId strings, same as the
    // legacy filter passed it, so the cast mirrors that existing behavior.
    saveIngredientFilters([] as any, [] as any, [] as any, DEFAULT_SORT);
    refetchWithFilters([], [], [], DEFAULT_SORT);
  };

  const apply = () => {
    saveIngredientFilters(
      selectedIngredients as any,
      selectedSuppliers as any,
      selectedDataTypes as any,
      sortBy
    );
    refetchWithFilters(selectedIngredients, selectedSuppliers, selectedDataTypes, sortBy);
  };

  return {
    ingredientOptions,
    supplierOptions,
    dataTypeOptions,
    selectedIngredients,
    selectedSuppliers,
    selectedDataTypes,
    sortBy,
    toggleIngredient: value => setSelectedIngredients(prev => toggleValue(prev, value)),
    toggleSupplier: value => setSelectedSuppliers(prev => toggleValue(prev, value)),
    toggleDataType: value => setSelectedDataTypes(prev => toggleValue(prev, value)),
    setSortBy,
    reset,
    apply
  };
};
